import { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import { getWorkflowSettings, updateWorkflowSettings } from "api";
import Button from "components/atoms/Button/Button";
import Input from "components/atoms/Input/Input";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import "./companyDetails.scss";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import StepperInput from "components/atoms/StepperInput/StepperInput";

interface IWorkflowSettings {
  asap_delivery_option_enabled: boolean | string;
  use_default_confirmation_time: boolean | string;
  default_confirmation_time_days: number;
  incoterm_enabled: string;
  minimum_invites: {
    freight_mode: string;
    minimum_invited_providers: number;
  }[];
}

function WorkflowSettingsForm() {
  const intl = useIntl();
  const methods = useForm<IWorkflowSettings>({ shouldFocusError: true });
  const { setValue, getValues, handleSubmit, watch } = methods;
  watch();

  const { permissions } = usePermissions();
  const {
    permissions: {
      admin_settings: { edit_company_settings },
    },
  } = JSON.parse(permissions || "{}");

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [freightModes, setFreightModes] = useState([]);

  const incotermOptions = [
    {
      value: "off",
      label: intl.formatMessage({
        id: "general.off",
        defaultMessage: "Off",
      }),
    },
    {
      value: "on_optional",
      label: intl.formatMessage({
        id: "settings.workFlow.onOptional",
        defaultMessage: "On optional",
      }),
    },
    {
      value: "on_mandatory",
      label: intl.formatMessage({
        id: "settings.workFlow.onMandatory",
        defaultMessage: "On mandatory",
      }),
    },
  ];

  const getSettings = () => {
    setIsLoading(true);
    getWorkflowSettings()
      .then((res) => {
        if (res.status === 200) {
          setValue("asap_delivery_option_enabled", res.data.asap_delivery_option_enabled);
          setValue("use_default_confirmation_time", res.data.use_default_confirmation_time);
          setValue("default_confirmation_time_days", res.data.default_confirmation_time_days);
          setValue("incoterm_enabled", res.data.incoterm_enabled);
          res.data.minimum_invites.forEach((item: any) => {
            setValue(`minimum_invites.${item.freight_mode}`, item.minimum_invited_providers);
          });
          setFreightModes(res.data.minimum_invites);
        }
        setIsLoading(false);
      })
      .catch((err) => errorToast(err));
  };

  useEffect(() => {
    getSettings();
  }, []);

  const onSubmit = (data: IWorkflowSettings) => {
    setIsSubmitting(true);
    data.minimum_invites = Object.entries(data.minimum_invites).map(([key, value]) => ({
      freight_mode: key,
      minimum_invited_providers: Number(value),
    }));
    updateWorkflowSettings(data)
      .then((res) => {
        if (res.status === 200) {
          toast(
            intl.formatMessage({
              id: "settings.settingsUpdated",
              defaultMessage: "Settings updated successfully",
            }),
            {
              type: "success",
            },
          );
          getSettings();
        }
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSubmitting(false));
  };

  if (isLoading) return <LoadingSkeleton noShadow rows={2} />;

  return (
    <FormProvider {...methods}>
      <form className="border-bottom-light border-top-light">
        <Row
          style={{
            padding: "16px 0",
          }}
        >
          <Col sm={3}>
            <div className="font-weight-bold font-b1">
              <FormattedMessage id="settings.workFlow" defaultMessage="Workflow Settings" />
            </div>
            <span className=" font-b3">
              <FormattedMessage
                id="settings.company-details.workflowSettingsSubtext"
                defaultMessage="Please provide the quote request form settings"
              />
            </span>
          </Col>
          <Col sm={9} className="d-flex flex-column font-b3">
            <div className="mb-3 mt-3 mt-md-0">
              <Checkbox
                providerSections
                name="asap_delivery_option_enabled"
                checkboxLabel={intl.formatMessage({
                  id: "settings.workFlow.asapDelivery",
                  defaultMessage: "ASAP for delivery",
                })}
                disabled={!edit_company_settings}
              />
            </div>
            <div className="mb-3">
              <Checkbox
                providerSections
                name="use_default_confirmation_time"
                checkboxLabel={intl.formatMessage({
                  id: "settings.company-details.defaultConfirmationTime",
                  defaultMessage: "Default confirmation time",
                })}
                disabled={!edit_company_settings}
              />
            </div>

            {getValues("use_default_confirmation_time") && (
              <Row className="mb-3 m-0">
                <div className="font-b3 mb-2 required">
                  <FormattedMessage
                    id="settings.invoice-settings.defaultConfirmationDays"
                    defaultMessage="Default confirmation days"
                  />
                </div>
                <Input
                  required
                  className="input-width-md rounded-input"
                  type="number"
                  name="default_confirmation_time_days"
                  eventContext="Settings - Company Details"
                  disabled={!edit_company_settings}
                />
              </Row>
            )}

            <Row className="m-0 d-flex flex-column">
              <div className="font-b3 mb-2">
                <FormattedMessage id="settings.invoice-settings.incotermsEnabled" defaultMessage="Incoterms enabled" />
              </div>
              <Dropdown
                className="w-192"
                useHookForm
                options={incotermOptions}
                name="incoterm_enabled"
                disabled={!edit_company_settings}
              />
            </Row>

            <div className="font-weight-bold mt-3 mb-2">
              <FormattedMessage id="settings.workFlow.minimumInvites" defaultMessage="Minimum invites" />
            </div>
            <div className="d-flex flex-wrap">
              {freightModes &&
                freightModes.map((item: { freight_mode: string; minimum_invited_providers: number }) => {
                  const formattedLabel = item.freight_mode
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
                  return (
                    <div key={item.freight_mode} className="d-flex flex-column mb-3 mr-3">
                      <div className="mb-1 font-b3">{formattedLabel}</div>
                      <StepperInput
                        name={`minimum_invites.${item.freight_mode}`}
                        defaultValue={item.minimum_invited_providers}
                        disabled={!edit_company_settings}
                      />
                    </div>
                  );
                })}
            </div>
            <Button
              className="btn-secondary"
              onClick={handleSubmit(onSubmit)}
              isLoading={isSubmitting}
              style={{
                width: "106px",
                whiteSpace: "nowrap",
              }}
              disabled={!edit_company_settings}
            >
              <FormattedMessage id="general.saveChanges" defaultMessage="Save changes" />
            </Button>
          </Col>
        </Row>
      </form>
    </FormProvider>
  );
}

export default WorkflowSettingsForm;
